/**
 * Standalone substrate-v2 capture against a Playwright page.
 *
 * Free function (not a method) so callers that don't hold a
 * `PlaywrightClient` instance — e.g. selector-agent drain pages
 * created via `browserContext.newPage()` — can capture v2 substrate
 * without instantiating a full client.
 *
 * `PlaywrightClient.captureSubstrateV2` delegates here so there is
 * exactly one implementation.
 *
 * @see playwright-client.ts (PlaywrightClient.captureSubstrateV2)
 * @module
 */

import type { Page } from 'playwright';
import type { BrowserLogger } from '../logger.js';
import { buildCssHint } from './css-hint-filter.js';
import { collectFrameSelectors, appendV2FrameSelectors } from './frame-walker.js';
import { runPerRefAugmentations } from './per-ref-augmentation.js';
import {
  appendV2RoleOverrides,
  appendSubstrateV2DerivedAttrs,
} from './snapshot-substrate-v2.js';

/**
 * Capture a substrate-v2 YAML snapshot of the given page,
 * independent of any per-session flag state.
 *
 * Pure read: does NOT mutate the DOM. The v2 enrichers used here
 * (modal role overrides, frame walker, derived-attr post-pass) are
 * all non-mutating.
 */
export async function captureSubstrateV2(
  page: Page,
  logger: BrowserLogger
): Promise<string> {
  let raw: string | undefined;
  const maxRetries = 3;
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      raw = await page.ariaSnapshot({ mode: 'ai' });
      break;
    } catch (err) {
      if (attempt < maxRetries) {
        await new Promise((resolve) => setTimeout(resolve, 200 * attempt));
      } else {
        logger.warn('[captureSubstrateV2] ariaSnapshot failed, using empty fallback', {
          attempts: maxRetries,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }
  }
  let yaml = raw ?? '- page: (empty)';

  // Single consolidated per-ref pass: editability, gridCellValue,
  // formFieldLabel, filterState, modalV2. One `aria-ref=` resolve
  // per ref + one batched evaluate, regardless of how many checks
  // each ref needs. The legacy chain of five separate evaluates is
  // gone.
  const augmented = await runPerRefAugmentations(page, yaml, logger, { v2: true });
  yaml = augmented.yaml;
  if (augmented.v2ModalOverrides.length > 0) {
    yaml = appendV2RoleOverrides(
      yaml,
      augmented.v2ModalOverrides.map((o) => ({
        ref: o.ref,
        augRole: o.augRole,
        augName: o.augName,
        cssHint: buildCssHint(o.cssTag, o.cssClasses ?? []),
      }))
    );
  }

  const frameByRef = await collectFrameSelectors(page, yaml, logger);
  if (frameByRef.size > 0) {
    yaml = appendV2FrameSelectors(yaml, frameByRef);
  }

  yaml = appendSubstrateV2DerivedAttrs(yaml);
  return yaml;
}
